import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { AnalyticEvents, PowerLaneAppAnalytics } from '@quorum/models/xs-misc';
import { AppAnalytics, TransactionTypes } from '@quorum/models/xs-resource';
import { of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import * as PowerlaneAnalyticsActions from './powerlane-analytics.actions';

@Injectable()
export class PowerlaneAnalyticsEffects {
  AppointmentCreatedFromPowerLane$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerlaneAnalyticsActions.AppointmentCreatedFromPowerLane),
      fetch({
        run: (action) => {
          return this.authenticationStateService.selectAuthenticatedUser().pipe(
            filter((authUser) => authUser != null),
            take(1),
            map((authUser) => {
              const analyticsEvent: PowerLaneAppAnalytics = {
                ...new PowerLaneAppAnalytics(),
                custId: authUser.user.destination,
                employeeId: authUser.user.xselleratorEmployeeId,
                transactionType: TransactionTypes.ServiceAppointment,
                transactionId: action.appointmentId.toString(),
                event: AnalyticEvents.CREATED,
              };

              return PowerlaneAnalyticsActions.SendAnalyticsToServer({ analyticsEvent });
            })
          );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return PowerlaneAnalyticsActions.AnalyticsError({ error });
        },
      })
    )
  );

  WorkOrderCreatedFromPowerLane$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerlaneAnalyticsActions.WorkOrderCreatedFromPowerLane),
      fetch({
        run: (action) => {
          return this.authenticationStateService.selectAuthenticatedUser().pipe(
            filter((authUser) => authUser != null),
            take(1),
            map((authUser) => {
              const analyticsEvent: PowerLaneAppAnalytics = {
                ...new PowerLaneAppAnalytics(),
                custId: authUser.user.destination,
                employeeId: authUser.user.xselleratorEmployeeId,
                transactionType: TransactionTypes.WorkOrder,
                transactionId: action.workOrderId.toString(),
                event: AnalyticEvents.CREATED,
              };

              return PowerlaneAnalyticsActions.SendAnalyticsToServer({ analyticsEvent });
            })
          );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return PowerlaneAnalyticsActions.AnalyticsError({ error });
        },
      })
    )
  );

  VcrSentFromWorkOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerlaneAnalyticsActions.VcrSentFromWorkOrder),
      fetch({
        run: (action) => {
          return this.authenticationStateService.selectAuthenticatedUser().pipe(
            filter((authUser) => authUser != null),
            take(1),
            map((authUser) => {
              const analyticsEvent: PowerLaneAppAnalytics = {
                ...new PowerLaneAppAnalytics(),
                custId: authUser.user.destination,
                employeeId: authUser.user.xselleratorEmployeeId,
                transactionType: TransactionTypes.WorkOrder,
                transactionId: action.workOrderId.toString(),
                event: AnalyticEvents.VCRSENT,
              };

              return PowerlaneAnalyticsActions.SendAnalyticsToServer({ analyticsEvent });
            })
          );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return PowerlaneAnalyticsActions.AnalyticsError({ error });
        },
      })
    )
  );

  VcrSentWithMediaFromWorkOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerlaneAnalyticsActions.VcrSentWithMediaFromWorkOrder),
      fetch({
        run: (action) => {
          return this.authenticationStateService.selectAuthenticatedUser().pipe(
            filter((authUser) => authUser != null),
            take(1),
            map((authUser) => {
              const analyticsEvent: PowerLaneAppAnalytics = {
                ...new PowerLaneAppAnalytics(),
                custId: authUser.user.destination,
                employeeId: authUser.user.xselleratorEmployeeId,
                transactionType: TransactionTypes.WorkOrder,
                transactionId: action.workOrderId.toString(),
                event: AnalyticEvents.VCRWITHMEDIASENT,
              };

              return PowerlaneAnalyticsActions.SendAnalyticsToServer({ analyticsEvent });
            })
          );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return PowerlaneAnalyticsActions.AnalyticsError({ error });
        },
      })
    )
  );

  MediaAddedToWorkOrderFromPowerLane$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerlaneAnalyticsActions.MediaAddedToWorkOrderFromPowerLane),
      fetch({
        run: (action) => {
          return this.authenticationStateService.selectAuthenticatedUser().pipe(
            filter((authUser) => authUser != null),
            take(1),
            map((authUser) => {
              const analyticsEvent: PowerLaneAppAnalytics = {
                ...new PowerLaneAppAnalytics(),
                ...(action.videoDuration ? { videoDuration: action.videoDuration } : {}),
                custId: authUser.user.destination,
                employeeId: authUser.user.xselleratorEmployeeId,
                mediaAddedFromLocation: action.mediaAddedFromLocation,
                transactionType: TransactionTypes.WorkOrder,
                transactionId: action.workOrderId.toString(),
                event:
                  action.fileType === 'image'
                    ? AnalyticEvents.MEDIAIMAGEADDED
                    : action.fileType === 'video'
                    ? AnalyticEvents.MEDIAVIDEOADDED
                    : AnalyticEvents.MEDIAADDED,
              };

              return PowerlaneAnalyticsActions.SendAnalyticsToServer({ analyticsEvent });
            })
          );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return PowerlaneAnalyticsActions.AnalyticsError({ error });
        },
      })
    )
  );

  SendAnalyticsToServer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PowerlaneAnalyticsActions.SendAnalyticsToServer),
        fetch({
          run: (action) => {
            if (this.apiService.apiBase.indexOf('localhost') == -1) {
              return this.apiService.post<AppAnalytics>(`public/analytics`, action.analyticsEvent);
            } else {
              return of(null);
            }
          },

          onError: (action, error) => {
            console.error('Error', error);
            return PowerlaneAnalyticsActions.AnalyticsError({ error });
          },
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private authenticationStateService: AuthenticationStateService,
    private apiService: ApiService
  ) {}
}
